// src/components/themes/default/admin/BranchCategoriesTab.tsx
"use client";

import { useState } from "react";
import Swal from "sweetalert2";
import {
  Layers,
  PlusCircle,
  Search,
  Sparkles,
  Package,
  Trash2,
  Edit2,
  X,
  Upload,
  Loader2,
  Check,
} from "lucide-react";

interface BranchCategoriesTabProps {
  categories: any[];
  subdomain: string;
  onOpenModal: () => void;
  onRefresh: () => void;
  branchId?: number;
  theme?: "dark" | "light";
  primaryColor?: string;
}

export default function BranchCategoriesTab({
  categories = [],
  subdomain,
  onOpenModal,
  onRefresh,
  branchId,
  theme = "dark",
  primaryColor = "#059669",
}: BranchCategoriesTabProps) {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<"all" | "home" | "hidden">("all");

  // Edit Modal State
  const [editCategory, setEditCategory] = useState<any | null>(null);
  const [editName, setEditName] = useState("");
  const [editIcon, setEditIcon] = useState("📦");
  const [editShowOnHome, setEditShowOnHome] = useState(true);
  const [editDescription, setEditDescription] = useState("");
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const getApiUrl = (endpoint: string) => {
    const isLocal =
      typeof window !== "undefined" &&
      (window.location.hostname.includes("localhost") ||
        window.location.hostname === "127.0.0.1");
    return isLocal
      ? `http://${subdomain}.localhost:8000${endpoint}`
      : endpoint;
  };

  const getAuthToken = () => {
    if (typeof window === "undefined") return "";

    const pathParts = window.location.pathname.split("/b/");
    const currentBranchSlug = pathParts[1]?.split("/")[0]?.split("?")[0] || "";

    if (currentBranchSlug) {
      const branchAuth = localStorage.getItem(`branch_auth_${subdomain}_${currentBranchSlug}`);
      if (branchAuth) {
        try {
          const parsed = JSON.parse(branchAuth);
          if (parsed.token) return parsed.token;
        } catch {}
      }
    }

    return (
      localStorage.getItem("merchant_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("access_token") ||
      ""
    );
  };

  const getAuthHeaders = () => {
    const token = getAuthToken();
    const activeBranchId = branchId || localStorage.getItem("active_branch_id") || "";

    return {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(activeBranchId ? { "X-Branch-ID": String(activeBranchId) } : {}),
    };
  };

  // ১. ওপেন এডিট মোডাল
  const handleOpenEdit = (cat: any) => {
    setEditCategory(cat);
    setEditName(cat.name || "");
    setEditIcon(cat.icon || "📦");
    setEditShowOnHome(cat.show_on_homepage !== false);
    setEditDescription(cat.description || "");
    setEditImageFile(null);
    setEditImagePreview(cat.image || null);
    setEditError(null);
  };

  // ২. ক্যাটাগরি এডিট সাবমিশন
  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editCategory) return;
    setEditLoading(true);
    setEditError(null);

    try {
      const formData = new FormData();
      formData.append("name", editName.trim());
      formData.append("icon", editIcon.trim());
      formData.append("description", editDescription.trim());
      formData.append("show_on_homepage", String(editShowOnHome));

      const activeBranch = branchId || localStorage.getItem("active_branch_id");
      if (activeBranch) {
        formData.append("branch_id", String(activeBranch));
      }

      if (editImageFile) {
        formData.append("image", editImageFile);
      }

      const res = await fetch(getApiUrl(`/api/v1/stores/categories/${editCategory.id}/`), {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || data.name?.[0] || "ক্যাটাগরি আপডেট করা সম্ভব হয়নি।");
      }

      setEditCategory(null);

      // 🌟 Top SweetAlert Success
      Swal.fire({
        position: "top",
        icon: "success",
        title: "ক্যাটাগরি আপডেট সফল!",
        text: `"${editName}" ক্যাটাগরির তথ্য সংরক্ষিত হয়েছে।`,
        showConfirmButton: false,
        timer: 2000,
        background: "#090d16",
        color: "#ffffff",
        customClass: {
          popup: "rounded-3xl border border-slate-800 shadow-2xl mt-5",
        },
      });

      onRefresh();
    } catch (err: any) {
      setEditError(err.message || "Failed to update category");
    } finally {
      setEditLoading(false);
    }
  };

  // 🌟 ৩. ক্যাটাগরি ডিলিট হ্যান্ডলার (SweetAlert Top Confirmation)
  const handleDeleteCategory = async (cat: any) => {
    const result = await Swal.fire({
      title: "আপনি কি নিশ্চিত?",
      text: `"${cat.name}" ক্যাটাগরি এবং এর তথ্য ডিলিট করা হবে!`,
      icon: "warning",
      position: "top",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#334155",
      confirmButtonText: "হ্যাঁ, ডিলিট করুন!",
      cancelButtonText: "বাতিল",
      background: "#090d16",
      color: "#ffffff",
      iconColor: "#f59e0b",
      customClass: {
        popup: "rounded-3xl border border-slate-800 shadow-2xl mt-6",
        title: "text-base font-black text-white",
        htmlContainer: "text-xs text-slate-400",
        confirmButton: "px-5 py-2.5 rounded-2xl text-xs font-black shadow-lg cursor-pointer",
        cancelButton: "px-5 py-2.5 rounded-2xl text-xs font-bold cursor-pointer",
      },
    });

    if (!result.isConfirmed) return;

    try {
      const res = await fetch(getApiUrl(`/api/v1/stores/categories/${cat.id}/`), {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (res.ok || res.status === 204) {
        Swal.fire({
          position: "top",
          icon: "success",
          title: "ক্যাটাগরি অপসারিত হয়েছে!",
          text: `"${cat.name}" সফলভাবে ডিলিট হয়েছে।`,
          showConfirmButton: false,
          timer: 2000,
          background: "#090d16",
          color: "#ffffff",
          customClass: {
            popup: "rounded-3xl border border-slate-800 shadow-2xl mt-5",
          },
        });
        onRefresh();
      } else {
        Swal.fire({
          position: "top",
          icon: "error",
          title: "ডিলিট ব্যর্থ হয়েছে",
          text: "ক্যাটাগরি মোছা সম্ভব হয়নি।",
          background: "#090d16",
          color: "#ffffff",
          customClass: {
            popup: "rounded-3xl border border-slate-800 shadow-2xl mt-5",
          },
        });
      }
    } catch {
      Swal.fire({
        position: "top",
        icon: "error",
        title: "সার্ভার এরর",
        text: "সার্ভারে যোগাযোগ করা যায়নি।",
        background: "#090d16",
        color: "#ffffff",
        customClass: {
          popup: "rounded-3xl border border-slate-800 shadow-2xl mt-5",
        },
      });
    }
  };

  const filtered = categories.filter((c) => {
    const matchSearch =
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.slug?.toLowerCase().includes(search.toLowerCase());
    if (filterType === "home") return matchSearch && c.show_on_homepage;
    if (filterType === "hidden") return matchSearch && !c.show_on_homepage;
    return matchSearch;
  });

  return (
    <div className="space-y-5 animate-fadeIn">
      {/* Header bar */}
      <div className="p-6 rounded-3xl bg-slate-900/90 border border-slate-800 text-white flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              <Layers className="w-5 h-5" />
            </div>
            <h1 className="text-xl font-black">Branch Categories</h1>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Create, edit, and organize outlet product collections ({categories.length} total categories)
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onOpenModal}
            className="flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black text-white bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 shadow-lg shadow-rose-600/30 transition-all cursor-pointer"
          >
            <PlusCircle className="w-4 h-4" />
            <span>+ Add Category</span>
          </button>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search category name, slug..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl text-xs font-bold bg-slate-950 border border-slate-800 text-white outline-none focus:border-cyan-500"
          />
        </div>

        {/* Quick filter pills */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setFilterType("all")}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              filterType === "all"
                ? "bg-rose-500 text-white"
                : "bg-slate-950 text-slate-400 hover:text-white"
            }`}
          >
            All Categories ({categories.length})
          </button>
          <button
            onClick={() => setFilterType("home")}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              filterType === "home"
                ? "bg-cyan-500 text-white"
                : "bg-slate-950 text-slate-400 hover:text-white"
            }`}
          >
            Homepage Visible
          </button>
          <button
            onClick={() => setFilterType("hidden")}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              filterType === "hidden"
                ? "bg-slate-700 text-white"
                : "bg-slate-950 text-slate-400 hover:text-white"
            }`}
          >
            Hidden
          </button>
        </div>
      </div>

      {/* Categories Cards Grid with Edit & Delete */}
      {filtered.length === 0 ? (
        <div className="py-20 text-center rounded-3xl bg-slate-900/40 border border-dashed border-slate-800">
          <Layers className="w-12 h-12 mx-auto text-slate-600 mb-3" />
          <h3 className="text-sm font-black text-slate-300">No Branch Categories Found</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
            এই আউটলেটে এখনও কোনো ক্যাটাগরি তৈরি করা হয়নি। উপরের "+ Add Category" ক্লিক করুন।
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((cat) => (
            <div
              key={cat.id}
              className="p-4 rounded-3xl bg-slate-900 border border-slate-800/80 hover:border-slate-700 transition-all flex flex-col justify-between group shadow-lg"
            >
              <div>
                {/* Banner / Icon area */}
                <div className="relative w-full h-28 rounded-2xl overflow-hidden bg-slate-950 border border-slate-800 mb-3 flex items-center justify-center">
                  {cat.image ? (
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="text-3xl">{cat.icon || "📦"}</div>
                  )}

                  <span
                    className={`absolute top-2 right-2 px-2 py-0.5 rounded-md text-[9px] font-black uppercase backdrop-blur-md ${
                      cat.show_on_homepage
                        ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                        : "bg-slate-800/80 text-slate-400"
                    }`}
                  >
                    {cat.show_on_homepage ? "Homepage Live" : "Hidden"}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-lg shrink-0">{cat.icon || "📦"}</span>
                  <h3 className="text-sm font-black text-white truncate" title={cat.name}>
                    {cat.name}
                  </h3>
                </div>

                <p className="text-[11px] font-mono text-cyan-400/80 mt-1 truncate">
                  /{cat.slug}
                </p>

                {cat.description && (
                  <p className="text-[11px] text-slate-400 line-clamp-2 mt-2 leading-relaxed">
                    {cat.description}
                  </p>
                )}
              </div>

              {/* 🌟 Action Bar: Edit & Delete buttons */}
              <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
                <span className="text-[10px] font-mono text-slate-500 font-bold">
                  ID: #{cat.id}
                </span>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleOpenEdit(cat)}
                    className="p-1.5 rounded-xl bg-slate-950 text-amber-400 hover:text-white hover:bg-amber-600/30 border border-amber-500/20 transition-colors cursor-pointer"
                    title="Edit Category"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={() => handleDeleteCategory(cat)}
                    className="p-1.5 rounded-xl bg-slate-950 text-rose-400 hover:text-white hover:bg-rose-600/30 border border-rose-500/20 transition-colors cursor-pointer"
                    title="Delete Category"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* =================================================================== */}
      {/* 🌟 EDIT CATEGORY MODAL */}
      {/* =================================================================== */}
      {editCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="w-full max-w-md rounded-3xl bg-[#090d16] border border-slate-800/80 text-white shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/60 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  <Edit2 className="w-4 h-4" />
                </div>
                <h2 className="text-sm font-black tracking-wide">Edit Category</h2>
              </div>
              <button
                onClick={() => setEditCategory(null)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form
              id="edit-branch-category-form"
              onSubmit={handleSaveEdit}
              className="flex-1 overflow-y-auto p-6 space-y-4 no-scrollbar"
            >
              {editError && (
                <div className="p-3 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-bold leading-relaxed">
                  {editError}
                </div>
              )}

              <div>
                <label className="block text-[11px] font-bold text-slate-300 mb-1.5">
                  Category Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-2xl text-xs font-bold bg-[#0d1424] border border-amber-500/40 text-white outline-none focus:border-amber-400 transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 items-center">
                <div>
                  <label className="block text-[11px] font-bold text-slate-300 mb-1.5">
                    Icon / Emoji
                  </label>
                  <div className="flex items-center gap-2 bg-[#0d1424] border border-slate-800 rounded-2xl p-1.5 px-3">
                    <span className="text-base">{editIcon}</span>
                    <input
                      type="text"
                      value={editIcon}
                      onChange={(e) => setEditIcon(e.target.value)}
                      className="w-full text-xs font-bold bg-transparent text-white outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-300 mb-1.5">
                    Homepage Grid
                  </label>
                  <label className="flex items-center gap-2 bg-[#0d1424] border border-slate-800 rounded-2xl p-2.5 px-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editShowOnHome}
                      onChange={(e) => setEditShowOnHome(e.target.checked)}
                      className="w-4 h-4 accent-amber-500 rounded cursor-pointer"
                    />
                    <span className="text-xs font-bold text-slate-300">Show on Home</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-300 mb-1.5 block">Description</label>
                <textarea
                  rows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full p-3 rounded-2xl text-xs font-bold bg-[#0d1424] border border-slate-800 text-white outline-none focus:border-amber-500/50 resize-none transition-all"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-300 mb-1.5">
                  Category Banner Image
                </label>
                <div className="relative border-2 border-dashed border-slate-800 hover:border-amber-500/40 rounded-2xl p-4 text-center transition-colors bg-[#0d1424]/50">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        const file = e.target.files[0];
                        setEditImageFile(file);
                        setEditImagePreview(URL.createObjectURL(file));
                      }
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  {editImagePreview ? (
                    <div className="relative w-full h-24 rounded-xl overflow-hidden">
                      <img src={editImagePreview} alt="Preview" className="w-full h-full object-cover" />
                      <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/80 text-[10px] text-amber-400 font-bold">
                        ✓ Selected
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 py-1 text-slate-400">
                      <Upload className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-bold text-slate-300">Change image</span>
                    </div>
                  )}
                </div>
              </div>
            </form>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-800/60 bg-[#070a12] shrink-0">
              <button
                type="button"
                onClick={() => setEditCategory(null)}
                className="px-4 py-2 rounded-2xl text-xs font-bold text-slate-400 hover:bg-slate-800 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="edit-branch-category-form"
                disabled={editLoading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black text-white bg-amber-600 hover:bg-amber-500 shadow-lg shadow-amber-600/30 transition-all cursor-pointer disabled:opacity-50"
              >
                {editLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}